import { useEffect, useMemo } from 'react';
import { Label } from '@/components/ui/label';

/** Generic two-step "provider → model" selector. Used for both chat models
 *  and image models so the picker isn't a 90-item flat list. The caller
 *  supplies the items + a `familyKey` (which field on each item encodes
 *  the provider) + a display label map. Selection value is a single id
 *  string; we derive which provider to show by looking the id up. */

export type ProviderItem = {
  id: string;
  label: string;
  /** Lucide-style suffix shown on the right (price, note, etc). */
  suffix?: string;
  deprecated?: boolean;
};

export type ProviderGroup<T extends ProviderItem> = {
  id: string;
  label: string;
  items: T[];
};

export function ProviderModelSelect<T extends ProviderItem>({
  groups,
  value,
  onChange,
  providerLabel = 'Sağlayıcı',
  modelLabel = 'Model',
  hideDeprecated = true,
}: {
  groups: ProviderGroup<T>[];
  value: string | undefined;
  onChange: (id: string) => void;
  providerLabel?: string;
  modelLabel?: string;
  hideDeprecated?: boolean;
}) {
  // Figure out which provider the current value belongs to.
  const selectedProvider = useMemo(() => {
    for (const g of groups) {
      if (g.items.some((i) => i.id === value)) return g.id;
    }
    return groups[0]?.id ?? '';
  }, [groups, value]);

  const currentGroup = groups.find((g) => g.id === selectedProvider);
  const visibleItems = (currentGroup?.items ?? []).filter(
    (i) => !hideDeprecated || !i.deprecated,
  );

  // When provider changes, jump to the first model in that provider so
  // the model dropdown is never showing a value that doesn't belong.
  useEffect(() => {
    if (!value || !visibleItems.some((i) => i.id === value)) {
      if (visibleItems.length > 0) onChange(visibleItems[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label className="text-xs">{providerLabel}</Label>
        <select
          value={selectedProvider}
          onChange={(e) => {
            // onChange is intentionally on the model picker; here we only
            // need to nudge the useEffect above to pick the new group's
            // first model.
            const next = groups.find((g) => g.id === e.target.value);
            if (next) {
              const first = next.items.filter((i) => !hideDeprecated || !i.deprecated)[0];
              if (first) onChange(first.id);
            }
          }}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {groups.map((g) => {
            const count = g.items.filter((i) => !hideDeprecated || !i.deprecated).length;
            if (count === 0) return null;
            return (
              <option key={g.id} value={g.id}>
                {g.label} ({count})
              </option>
            );
          })}
        </select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">{modelLabel}</Label>
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {visibleItems.map((i) => (
            <option key={i.id} value={i.id}>
              {i.label}
              {i.suffix ? ` — ${i.suffix}` : ''}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
